import { Globe } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useParams } from "@tanstack/react-router";
import { FeedbackTray } from "@/components/Feedback/FeedbackTray";
import { useFeatureFlags } from "@/contexts/FeatureFlagContext";
import type { Setting, WorldSetting } from "@/types/summary";
import { CollapsibleCard } from "./CollapsibleCard";
import { isInsightPresent } from "@/utils/isInsightPresent";
import { UnavailableInsightCard } from "./UnavailableInsightCard";
import { GlobalSettingCard } from "./GlobalSettingCard";
import { SettingSubcard } from "./SettingSubcard";

// Fallback for summaries created before the backend owned the display
// selection (ES-961). Should be removed after deployment and migration run
function selectVisibleSettingsFallback(settings: Setting[]): Setting[] {
  const MAX_SETTINGS = 5;
  const primaries = settings.filter((s) => s.setting_type === "primary");
  const majors = settings.filter((s) => s.setting_type === "major");
  return [...primaries, ...majors].slice(0, MAX_SETTINGS);
}

function selectVisibleSettings(worldSetting: WorldSetting): Setting[] {
  const settings = worldSetting.settings ?? [];
  if (!settings.length) {
    return [];
  }
  const selectedIndices = worldSetting.selected_setting_indices;
  if (Array.isArray(selectedIndices)) {
    return selectedIndices
      .filter((i) => Number.isInteger(i) && i >= 0 && i < settings.length)
      .map((i) => settings[i]);
  }
  return selectVisibleSettingsFallback(settings);
}

interface WorldAndSettingCardProps {
  worldSetting?: WorldSetting;
}

export function WorldAndSettingCard({
  worldSetting,
}: WorldAndSettingCardProps) {
  const { t } = useTranslation("jobDetails");
  const { jobId } = useParams({ from: "/jobs/$jobId", strict: true });
  const { features } = useFeatureFlags();

  if (!isInsightPresent.worldAndSetting(worldSetting)) {
    if (!features.worldAndSettings) return null;
    return <UnavailableInsightCard insight="worldAndSetting" />;
  }

  return (
    <FeedbackTray
      cardTestId="world-and-setting"
      jobId={jobId}
      contentType="world_setting"
      promptKey="feedback.worldSetting.prompt"
    >
      <CollapsibleCard
        icon={Globe}
        title={t("worldAndSettingCard.title")}
        testId="world-and-setting"
        showExperimentalBadge={true}
      >
        <div className="mt-5">
          <div className="space-y-4">
            <GlobalSettingCard worldSetting={worldSetting} />

            {selectVisibleSettings(worldSetting).map((setting, index) => (
              <SettingSubcard
                key={`${setting.location_label}-${setting.time_period_label}-${index}`}
                setting={setting}
                index={index}
              />
            ))}
          </div>
        </div>
      </CollapsibleCard>
    </FeedbackTray>
  );
}
